import React, { CSSProperties, FC, useState } from 'react';
import { css } from 'emotion';

import debounce from 'lodash/debounce';
import Input from '@vromo/ui/components/Input';
import LoadingIndicator from '@vromo/ui/components/LoadingIndicator';
import { noop } from 'lodash';
import { Coords } from '@vromo/models';

export interface GeocoderObject {
    center: [number, number];
    place_name: string;
    text: string;
}

const search = debounce((term, onChange, proximity) => {
    let url = `https://2mwssqe7ta.execute-api.us-east-1.amazonaws.com/prod/autocomplete?input=${term}`;

    if (proximity) url += `&proximity=${proximity.lat},${proximity.lon}`;

    fetch(url).then(async (response) => {
        onChange(await response.json());
    });
}, 300);

const getDetails = async (place: string) => {
    const url = `https://2mwssqe7ta.execute-api.us-east-1.amazonaws.com/prod/details?placeid=${place}`;
    const response = await fetch(url);

    return await response.json();
};

interface Props {
    onSelect: (o: GeocoderObject) => void;
    onChange?: (o: string) => void;
    initialValue?: string;
    label?: string;
    placeholder?: string;
    proximity?: Coords;
    style?: CSSProperties;
}

const Geocoder: FC<Props> = ({
    onSelect,
    onChange = noop,
    initialValue = '',
    label,
    placeholder,
    proximity,
    style = {}
}) => {
    const [loading, setLoading] = useState(false);
    const [focused, setFocus] = useState(false);
    const [query, setQuery] = useState(initialValue);
    const [results, setResults] = useState(null);

    return (
        <div style={{ position: 'relative', ...style }}>
            <Input
                type="text"
                label={label}
                value={query || ''}
                data-loading={loading}
                onChange={async (e) => {
                    setQuery(e.target.value);

                    if (e.target.value) {
                        setLoading(true);

                        search(
                            e.target.value,
                            (searchResults) => {
                                setLoading(false);
                                setResults(searchResults);
                            },
                            proximity
                        );
                    }

                    onChange(e.target.value);
                }}
                onFocus={() => setFocus(true)}
                onBlur={() => {
                    setTimeout(() => {
                        setFocus(false);
                    }, 200);
                }}
                placeholder={placeholder}
            />
            {loading ? (
                <div
                    style={{
                        position: 'absolute',
                        right: 10,
                        top: 15,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center'
                    }}>
                    <LoadingIndicator />
                </div>
            ) : null}
            {results && focused ? (
                <div
                    className={css`
                        position: absolute;
                        margin-top: -5px;
                        background-color: white;
                        z-index: 2;
                        width: 100%;
                        border: 1px solid #cacbcc;
                        border-radius: 5px;
                        overflow: hidden;
                    `}>
                    {results.predictions.map((it) => (
                        <div
                            key={it.id}
                            data-test-id="geocoder-result"
                            onClick={async () => {
                                setLoading(true);

                                const { lat, lng: lon } = (
                                    await getDetails(it.place_id)
                                ).result?.geometry?.location;

                                onSelect({
                                    center: [lon, lat],
                                    place_name: it.description,
                                    text: it.structured_formatting.main_text
                                });

                                setQuery(it.description);
                                setLoading(false);
                            }}
                            className={css`
                                cursor: pointer;
                                padding: 8px;

                                :hover {
                                    background-color: #f5f5f5;
                                }
                            `}>
                            {it.description}
                        </div>
                    ))}
                </div>
            ) : null}
        </div>
    );
};

export default Geocoder;
